import clsx from 'clsx';
import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import IAChatHeader from './components/IAChatHeader';
import IAChatHistory from './components/IAChatHistory';
import IAChatInput from './components/IAChatInput';
import IAChatMessages from './components/IAChatMessages';
import IAChatWelcome from './components/IAChatWelcome';
import { useIAChatPanelStyles } from './IAChatPanel.styles';
import { IAChatPanelProps } from './types';

const getAppHeaderOffset = () => {
  const header = document.querySelector('[data-redesign-app-header]');
  if (!header) return 0;

  // Viewport-relative: when the sticky header has scrolled away, bottom can be
  // negative and would push the fixed panel off-screen.
  const offset = Math.ceil(header.getBoundingClientRect().bottom);
  if (offset <= 0 || offset >= window.innerHeight) return 0;
  return offset;
};

const IAChatPanel = ({
  open,
  onMinimize,
  onClose,
  messages,
  inputValue,
  onInputChange,
  onSendMessage,
  onSelectPredefinedQuery,
  isLoading = false,
  viewMode,
  chatbotHistory,
  isLoadingHistory,
  onShowHistory,
  onBackToMain,
  onLoadConversation,
}: IAChatPanelProps) => {
  const classes = useIAChatPanelStyles();
  const [panelTop, setPanelTop] = useState(0);

  const updatePanelTop = useCallback(() => {
    setPanelTop(getAppHeaderOffset());
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updatePanelTop();
    window.addEventListener('resize', updatePanelTop);
    window.addEventListener('scroll', updatePanelTop, true);

    return () => {
      window.removeEventListener('resize', updatePanelTop);
      window.removeEventListener('scroll', updatePanelTop, true);
    };
  }, [open, updatePanelTop]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onMinimize();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onMinimize]);

  if (!open) {
    return null;
  }

  const showWelcome = viewMode === 'main' && messages.length === 0;
  const showMessages = viewMode !== 'history' && messages.length > 0;

  return createPortal(
    <>
      <div
        className={classes.backdrop}
        onClick={onMinimize}
        role="presentation"
        aria-hidden="true"
      />
      <aside
        className={classes.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Chat IA"
        style={{ '--ia-chat-panel-top': `${panelTop}px` } as CSSProperties}>
        <IAChatHeader
          viewMode={viewMode}
          showHistoryLink={viewMode === 'main'}
          onShowHistory={onShowHistory}
          onBackToMain={onBackToMain}
          onClose={onClose}
        />

        <div className={clsx(classes.body, showWelcome && classes.bodyWelcome)}>
          {viewMode === 'history' ? (
            <IAChatHistory
              chatbotHistory={chatbotHistory}
              isLoadingHistory={isLoadingHistory}
              onLoadConversation={onLoadConversation}
            />
          ) : showWelcome ? (
            <IAChatWelcome
              isLoading={isLoading}
              onSelectPredefinedQuery={onSelectPredefinedQuery}
            />
          ) : showMessages ? (
            <IAChatMessages messages={messages} isLoading={isLoading} />
          ) : null}
        </div>

        <IAChatInput
          inputValue={inputValue}
          onInputChange={onInputChange}
          onSendMessage={onSendMessage}
          isLoading={isLoading}
          disabled={isLoading}
        />
      </aside>
    </>,
    document.body,
  );
};

export default IAChatPanel;
